import AccountBootstrapGuard from '@/components/guards/AccountBootstrapGuard'
import SubscriptionGuard from '@/components/guards/SubscriptionGuard'
import SurveyGuard from '@/components/guards/SurveyGuard'
import { PostSignupPhoneGuard } from '@/components/guards/PostSignupPhoneGuard'
import { useContext, type ReactNode } from 'react'
import { AuthContext } from '@/context/auth-context'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import AccountSetupFlow from '@/account-setup/components/AccountSetupFlow'

type OnboardingFlowGateProps = {
  children: ReactNode
}

/**
 * The new account-setup flow must finish by setting the same `surveyStatus` claim as the existing
 * survey. That claim is what hands the user over to the existing guards.
 */
export function OnboardingFlowGate({ children }: OnboardingFlowGateProps) {
  const { user, userAccountData } = useContext(AuthContext)
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.ONBOARDING_REFRESH)

  const existingGuards = (
    <PostSignupPhoneGuard>
      <AccountBootstrapGuard>
        <SubscriptionGuard>
          <SurveyGuard>{children}</SurveyGuard>
        </SubscriptionGuard>
      </AccountBootstrapGuard>
    </PostSignupPhoneGuard>
  )

  const setupFinishedOrNotApplicable =
    userAccountData != null && (userAccountData.surveyCompleted || userAccountData.isOrgAccount || userAccountData.legacyDriveCompatibleOrganizationId != null)

  if (user == null || user.isAnonymous || setupFinishedOrNotApplicable) return existingGuards
  if (!loading && !enabled) return existingGuards
  // Waiting here keeps the existing guards from mounting (and starting checkout) for a user the flag sends to the new flow.
  if (loading || userAccountData == null) return <FullPageLoader />
  return <AccountSetupFlow />
}
